import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import PlusImage from '@fle-ui/plus-image';
import { useNavigate } from 'react-router-dom';
import { Icon, message } from '@/components';
import { CommonStore } from '@/stores';
import type { GetAdvertisingForModalParams } from '@/stores/common';

import './index.less';

export interface AdvertisingDataItem {
  id: string;
  image: string;
  link?: string;
}

export interface AdvertisingModalRef {
  open: (is_all?: 0 | 1) => void;
  close: () => void;
}

const AdvertisingModal = forwardRef<AdvertisingModalRef, {}>((_, ref) => {
  const navigate = useNavigate();
  const [advertisingData, setAdvertisingData] = useState<any[]>([]);

  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationPosition, setAnimationPosition] = useState({
    x: 0,
    y: 0,
    scale: 0.1,
    opacity: 0,
  });

  const modalRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    open: async (is_all = 0) => {
      // await waitTime(1000);
      getAdvertisingData(is_all);
    },
    close: () => {
      handleClose();
    },
  }));

  // 获取悬浮按钮的位置
  const getFloatingButtonPosition = () => {
    const floatingButton = document.getElementById('advertising-button');
    if (floatingButton) {
      const rect = floatingButton.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // 计算相对于视口中心的位置
      const viewportCenterX = window.innerWidth / 2;
      const viewportCenterY = window.innerHeight / 2;

      return {
        x: centerX - viewportCenterX,
        y: centerY - viewportCenterY,
        scale: 0.1,
        opacity: 0,
      };
    }
    return { x: 607, y: 429, scale: 0.1, opacity: 0 };
  };

  // 开始打开动画
  const startOpenAnimation = () => {
    const startPos = getFloatingButtonPosition();
    // 设置初始状态
    setAnimationPosition(startPos);

    requestAnimationFrame(() => {
      // 开始动画
      setAnimationPosition({ x: 0, y: 0, scale: 1, opacity: 1 });
    });
  };

  // 开始关闭动画
  const startCloseAnimation = () => {
    const endPos = getFloatingButtonPosition();

    // 先移动到悬浮按钮位置并缩小，同时变为透明
    setAnimationPosition(endPos);

    // 动画完成后关闭弹窗
    setTimeout(() => {
      setOpen(false);
      setAnimationPosition({ x: 0, y: 0, scale: 0.1, opacity: 0 });
    }, 300);
  };

  // 处理关闭
  const handleClose = () => {
    startCloseAnimation();
  };

  // 切换到上一张图片
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? advertisingData.length - 1 : prev - 1));
  };

  // 切换到下一张图片
  const handleNext = () => {
    setCurrentIndex((prev) => (prev === advertisingData.length - 1 ? 0 : prev + 1));
  };

  // 点击图片切换
  const handleImageClick = (index: number) => {
    const item = advertisingData[index];

    if (item && item.redirect_target) {
      if (item.redirect_type === 1) {
        if (item.redirect_target.startsWith('http')) {
          window.open(item.redirect_target, '_blank');
        } else {
          window.location.href = item.redirect_target;
        }
      }

      if (item.redirect_type === 2) {
        navigate(`/module/lgjProduct/platformSpecial/detail/${item.redirect_target}`);
      }

      CommonStore.modalClick(item.id);
      handleClose();
    }

    setCurrentIndex(index);
  };

  const getLocation = (): number | null => {
    switch (window.location.pathname) {
      case '/module/workbench/dashboard':
        return 1;
      default:
        return null;
    }
  };

  const getAdvertisingData = async (is_all: 0 | 1 = 0) => {
    const location = getLocation();
    const params: GetAdvertisingForModalParams = {
      is_all,
      platform: '0', // 0: 万鲤商联
    };
    if (location) {
      params.location = location;
    }
    const data = await CommonStore.getAdvertisingForModal(params);
    setAdvertisingData(data);

    if (data.length === 0) {
      is_all === 1 && message.warning('暂无活动，敬请期待~');
    } else {
      setOpen(true);
      startOpenAnimation();
    }
  };

  useEffect(() => {
    if (open) {
      // 弹窗广告显示埋点
      CommonStore.modalShow(advertisingData[currentIndex]?.id);
    } else if (!open) {
      setCurrentIndex(0);
      setAdvertisingData([]);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <>
      {/* 遮罩层 */}
      <div
        className="advertising-modal-mask"
        style={{
          opacity: animationPosition.opacity,
        }}
      />

      {/* 弹窗内容 */}
      <div className="advertising-modal-wrapper">
        <div
          ref={modalRef}
          className="advertising-modal-content"
          style={{
            transform: `translate(${animationPosition.x}px, ${animationPosition.y}px) scale(${animationPosition.scale})`,
            opacity: animationPosition.opacity,
          }}
        >
          {advertisingData.map((item, index) => (
            <div
              key={item.id}
              className={`advertising-image-item ${index === currentIndex ? 'active' : ''}`}
            >
              <PlusImage
                lazyLoad
                preview={false}
                src={item.image}
                ossProcess={{ width: 1920 }}
                onClick={() => handleImageClick(index)}
                proImageStyle={{ maxWidth: '100vh', maxHeight: '60vh' }}
                proImageClassName="advertising-image"
              />
              <Icon
                type="icon-guanbi"
                className="advertising-modal-close-icon"
                onClick={handleClose}
              />
            </div>
          ))}
        </div>
        {advertisingData.length > 1 && (
          <div
            className="advertising-modal-footer"
            style={{
              opacity: animationPosition.opacity,
            }}
          >
            <div className="advertising-modal-footer-left-btn" onClick={handlePrev}>
              <Icon type="icon-zuo" />
            </div>
            <div className="advertising-modal-footer-center">
              {currentIndex + 1} / {advertisingData.length}
            </div>
            <div className="advertising-modal-footer-right-btn" onClick={handleNext}>
              <Icon type="icon-you" />
            </div>
          </div>
        )}
      </div>
    </>
  );
});

AdvertisingModal.displayName = 'AdvertisingModal';

export default AdvertisingModal;
